/**
 * Tile layer URL presets for common map styles.
 * Import and use as `tileUrl` to switch between map themes.
 */
export const TILE_PRESETS = {
  /** Esri Dark Gray Canvas — default space ops aesthetic. */
  dark: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
  /** Esri World Imagery — natural satellite photos, makes the terminator dramatically visible. */
  satellite:
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  /** Esri Light Gray Canvas — light/neutral. */
  light:
    "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
} as const;

/** Primary basemap: Esri Dark Gray Canvas. */
export const DEFAULT_TILE =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";

/**
 * Place-name overlays for the Canvas basemaps, which bake labels into the base
 * tiles only at world scale. Drawn above the base so names survive past zoom ~6.
 */
export const CANVAS_LABEL_TILES: Record<string, string> = {
  [TILE_PRESETS.dark]:
    "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
  [TILE_PRESETS.light]:
    "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
};

/**
 * Esri Canvas (Dark/Light Gray) basemaps — the reference layers share these terms.
 * @see https://developers.arcgis.com/documentation/mapping-apis-and-services/deployment/basemap-attribution/
 */
export const ESRI_CANVAS_ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a> ' +
  "&mdash; Source: Esri, HERE, Garmin, &copy; OpenStreetMap contributors, and the GIS user community";

export const CARTO_ATTRIBUTION =
  '&copy; <a href="https://carto.com/attribution" target="_blank" rel="noopener noreferrer">CARTO</a>';

/** Fallback when primary tiles fail to avoid black tiles. */
export const FALLBACK_TILE =
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

export const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>';

/**
 * Esri World Imagery — required when using tiles from ArcGIS Online MapServer.
 * @see https://developers.arcgis.com/documentation/mapping-apis-and-services/deployment/basemap-attribution/
 */
export const ESRI_WORLD_IMAGERY_ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a> ' +
  "&mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

/**
 * Resolve HTML attribution for a basemap URL template (Leaflet `TileLayer` `attribution` option).
 * Unknown / custom URLs return an empty string — apps should document their own terms for those.
 */
export function getBasemapAttribution(tileUrl: string): string {
  const u = tileUrl.toLowerCase();
  if (u.includes("cartocdn")) return CARTO_ATTRIBUTION;
  if (u.includes("/canvas/")) return ESRI_CANVAS_ATTRIBUTION;
  if (u.includes("arcgisonline.com") || u.includes("arcgis.com"))
    return ESRI_WORLD_IMAGERY_ATTRIBUTION;
  if (u.includes("openstreetmap.org")) return OSM_ATTRIBUTION;
  return "";
}

/**
 * After this many consecutive tile errors, switch to the fallback layer.
 * Set to 1 so we bail out immediately on the first network failure instead
 * of waiting for several timeouts (which shows a black map for ~5 seconds).
 */
export const TILE_ERROR_THRESHOLD = 1;
